import { memo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { TrustpilotRating } from "./TrustpilotRating";
import { OptimizedImage } from "@/components/ui/OptimizedImage";
import { ArrowRight } from "lucide-react";
import { Link as RouterLink } from "react-router-dom";

interface DetailedLoanCardProps {
  provider: string;
  logo: string;
  loanAmount: string;
  nominalRate: string;
  effectiveRate: string;
  loanTerm: string;
  loanWithoutUC: string;
  responseTime: string;
  paymentSolution: string;
  link: string;
  rating?: number;
  reviewCount?: number;
  lenderBadge?: string;
}

export const DetailedLoanCard = memo(({
  provider,
  logo,
  loanAmount,
  nominalRate,
  effectiveRate,
  loanTerm,
  loanWithoutUC,
  responseTime,
  paymentSolution,
  link,
  rating,
  reviewCount,
  lenderBadge
}: DetailedLoanCardProps) => {
  return (
    <Card className="p-4 flex flex-col gap-3 hover:shadow-xl transition-all duration-300 cursor-pointer md:hover:-translate-y-2">
      {/* Header med provider-namn */}
      <div className="mb-2">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <h3 className="text-xl font-bold text-foreground">
            {provider}
          </h3>
          {lenderBadge && (
            <Badge className="bg-primary/10 text-primary border-primary/20 hover:bg-primary/20 text-xs">
              {lenderBadge}
            </Badge>
          )}
        </div>
        {rating && reviewCount && (
          <div className="flex items-center gap-2">
            <TrustpilotRating rating={rating} size="sm" showScore={true} showLogo={true} />
            <span className="text-xs text-muted-foreground">({reviewCount.toLocaleString('sv-SE')} omdömen)</span>
          </div>
        )}
      </div>

      {/* Detaljerad låneinformation */}
      <div className="space-y-2">
        <LoanInfoRow label="Lånebelopp" value={loanAmount} />
        <LoanInfoRow label="Nominell ränta" value={nominalRate} />
        <LoanInfoRow label="Effektiv ränta" value={effectiveRate} />
        <LoanInfoRow label="Löptid" value={loanTerm} />
        <LoanInfoRow label="Lån utan UC" value={loanWithoutUC} />
        <LoanInfoRow label="Svar på ansökan" value={responseTime} />
        <LoanInfoRow label="Betalningslösning" value={paymentSolution} />
      </div>

      {/* Grön "Se erbjudande" knapp */}
      <Button 
        size="lg" 
        className="w-full mt-3 bg-green-600 hover:bg-green-700 text-white"
        asChild
      >
        {link.startsWith('/') ? (
          <RouterLink to={link}>
            Läs mer om {provider} <ArrowRight className="ml-2 h-4 w-4 inline" />
          </RouterLink>
        ) : (
          <a href={link}>
            Läs mer om {provider} <ArrowRight className="ml-2 h-4 w-4 inline" />
          </a>
        )}
      </Button>
    </Card>
  );
});

// Helper komponent för info-rader
const LoanInfoRow = ({ label, value }: { label: string; value: string }) => (
  <div className="flex justify-between items-center py-1.5 border-b border-border last:border-0">
    <span className="text-xs text-muted-foreground">
      <span className="md:hidden">
        {label === "Betalningslösning" ? "Betalningslösn." : label}
      </span>
      <span className="hidden md:inline">
        {label}
      </span>
    </span>
    <span className="text-xs font-semibold text-right">{value}</span>
  </div>
);
